import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Pill } from './StateCard';

export interface GameCard {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  genre: string | null;
  platforms: string[];
  lifecycle: string;
  age_rating?: string | null;
  released_at?: string | null;
  featured?: boolean;
  new_release?: boolean;
  recently_updated?: boolean;
}

export const PLATFORM_LABEL: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  roblox: 'Roblox',
  steam: 'Steam',
  pc: 'PC',
  web: 'Web',
};

/**
 * Deterministic tile colours derived from the slug.
 *
 * This is a brand tile, not a fake screenshot: we never present generated art as
 * if it were gameplay. When a real key-art asset exists it replaces this.
 */
function tileColors(seed: string): { a: string; b: string } {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }

  const palettes = [
    { a: '#44b8ee', b: '#0f6f9b' },
    { a: '#6ecaf3', b: '#1590c6' },
    { a: '#f2a007', b: '#b45309' },
    { a: '#1ca9e8', b: '#0b3c56' },
    { a: '#5eead4', b: '#0f766e' },
    { a: '#a78bfa', b: '#5b21b6' },
  ];

  return palettes[hash % palettes.length];
}

/** Brand tile used wherever key art is not available. */
export function GameArt({ game, className }: { game: { slug: string; name: string }; className?: string }) {
  const { a, b } = tileColors(game.slug);
  // Sibling titles often share a prefix, so a parenthetical qualifier — when the
  // title has one — is what actually tells two tiles apart.
  const qualifier = game.name.match(/[(（]([^)）]+)[)）]/)?.[1];
  const source = (qualifier ?? game.name).replace(/[^\p{L}\p{N}]/gu, '');
  const initial = source.slice(0, 2) || '??';

  return (
    <div
      className={`tile ${className ?? ''}`}
      style={{ ['--tile-a' as string]: a, ['--tile-b' as string]: b }}
      role="img"
      aria-label={`${game.name} 대표 이미지`}
    >
      <span className="tile__initial">{initial}</span>
      <img className="tile__watermark" src="/ak-mark.png" alt="" aria-hidden="true" />
    </div>
  );
}

export function lifecycleTone(lifecycle: string): 'ok' | 'neutral' | 'warn' {
  if (lifecycle === 'live') return 'ok';
  if (lifecycle === 'sunset' || lifecycle === 'retired') return 'warn';
  return 'neutral';
}

export function lifecycleLabel(lifecycle: string): string {
  return {
    live: '서비스 중',
    coming_soon: '출시 예정',
    sunset: '종료 예정',
    retired: '서비스 종료',
  }[lifecycle] ?? lifecycle;
}

export function GameTile({ game, wide = false }: { game: GameCard; wide?: boolean }) {
  return (
    <Link to={`/games/${game.slug}`} className={`game-card${wide ? ' game-card--wide' : ''}`}>
      <GameArt game={game} className="game-card__art" />
      <div className="game-card__body">
        <h3 className="game-card__title">{game.name}</h3>
        {game.tagline && <p className="game-card__tagline">{game.tagline}</p>}
        <div className="game-card__meta">
          <Pill tone={lifecycleTone(game.lifecycle)}>{lifecycleLabel(game.lifecycle)}</Pill>
          {game.new_release && <Pill tone="info">신작</Pill>}
          {game.platforms.slice(0, 3).map((platform) => (
            <Pill key={platform} tone="neutral">{PLATFORM_LABEL[platform] ?? platform}</Pill>
          ))}
        </div>
      </div>
    </Link>
  );
}

export function SiteHead({
  eyebrow,
  title,
  lead,
  meta,
}: {
  eyebrow?: string;
  title: string;
  lead?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <header className="site-head">
      {eyebrow && <div className="site-head__eyebrow">{eyebrow}</div>}
      <h1 className="site-head__title">{title}</h1>
      {lead && <p className="site-head__lead">{lead}</p>}
      {meta && <div className="site-head__meta">{meta}</div>}
    </header>
  );
}

export function Section({
  title,
  description,
  moreHref,
  moreLabel = '전체 보기',
  children,
}: {
  title: string;
  description?: string;
  moreHref?: string;
  moreLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="section__head">
        <div>
          <h2 className="section__title">{title}</h2>
          {description && <p className="section__desc">{description}</p>}
        </div>
        {moreHref && <Link className="section__more" to={moreHref}>{moreLabel} →</Link>}
      </div>
      {children}
    </section>
  );
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {body && <div className="empty__body">{body}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function formatDate(value?: string | null, withTime = false): string {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export function relativeDate(value?: string | null): string {
  if (!value) return '—';
  const diffMs = Date.now() - new Date(value).getTime();
  const days = Math.floor(diffMs / 86_400_000);

  if (days <= 0) return '오늘';
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  if (days < 30) return `${Math.floor(days / 7)}주 전`;
  return formatDate(value);
}

/** Period progress bar. Shows nothing rather than guessing when dates are missing. */
export function Period({ startsAt, endsAt }: { startsAt?: string | null; endsAt?: string | null }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!startsAt || !endsAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [startsAt, endsAt]);

  if (!startsAt || !endsAt) {
    return <div className="period"><span>기간 미정</span></div>;
  }

  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  const pct = Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));

  return (
    <div className="period">
      <div className="period__bar">
        <div className="period__fill" style={{ width: `${pct}%` }} />
      </div>
      <span>{formatDate(startsAt)} – {formatDate(endsAt)}</span>
    </div>
  );
}

export function eventStateLabel(state: string): { label: string; tone: 'ok' | 'info' | 'neutral' } {
  return {
    active: { label: '진행 중', tone: 'ok' as const },
    upcoming: { label: '시작 전', tone: 'info' as const },
    ended: { label: '종료', tone: 'neutral' as const },
  }[state] ?? { label: state, tone: 'neutral' as const };
}

/**
 * User-facing labels for the server's state enums.
 *
 * The API returns the stored enum; these screens are read by players, so the
 * enum is translated at the edge rather than stored twice.
 */
export function incidentStateLabel(state: string): string {
  return {
    INVESTIGATING: '원인 확인 중',
    IDENTIFIED: '원인 파악',
    MONITORING: '조치 후 관찰',
    RESOLVED: '해결',
    investigating: '원인 확인 중',
    identified: '원인 파악',
    monitoring: '조치 후 관찰',
    resolved: '해결',
  }[state] ?? state;
}

export function ticketStatusLabel(status: string): string {
  return {
    OPEN: '접수됨',
    PENDING_USER: '답변 대기',
    IN_PROGRESS: '확인 중',
    RESOLVED: '해결됨',
    CLOSED: '종료',
  }[status] ?? status;
}

export function linkStateLabel(state: string): string {
  return { linked: '연결됨', pending: '확인 대기', revoked: '해제됨' }[state] ?? state;
}

export function entitlementStateLabel(state: string): string {
  return {
    ACTIVE: '사용 가능',
    EXPIRED: '기간 만료',
    REVOKED: '회수됨',
    PENDING: '확인 중',
    GRANTED: '지급됨',
    CLAIMED: '수령 완료',
    NONE: '해당 없음',
  }[state] ?? state;
}

export function dataRequestStateLabel(state: string): string {
  return {
    RECEIVED: '접수됨',
    IDENTITY_PENDING: '본인 확인 대기',
    PROCESSING: '처리 중',
    IN_PROGRESS: '처리 중',
    COMPLETED: '완료',
    PARTIALLY_COMPLETED: '일부 완료',
    REJECTED: '거절',
    CANCELLED: '취소됨',
  }[state] ?? state;
}

export function severityLabel(severity: string): string {
  return { minor: '경미', major: '중대', critical: '심각' }[severity] ?? severity;
}

/** Feature keys the status page reports on, in the words a player would use. */
export function featureLabel(feature: string): string {
  return {
    notices: '공지',
    rewards: '보상 지급',
    gifts: '선물',
    purchases: '구매',
    purchase: '구매',
    ads: '광고 보상',
    cloud_saves: '클라우드 저장',
    saves: '저장',
    leaderboards: '랭킹',
    login: '로그인',
    support: '고객지원',
  }[feature] ?? feature;
}
